import { FormConfig, FormValues, NextFunction } from '@topdanmark-onlinesolutions/claim-form-types';
import { getValueOrDefault } from '../components/values/Get';
import { isCustomerClaimCovered } from '../helpers/business-rules/isCustomerClaimCovered';
import { isCustomerAllowedToSelectWorkshop } from '../helpers/business-rules/isCustomerAllowedToSelectWorkshop';
import {
    getClaimDetails,
    getWhatHasBeenDamaged,
    getWorkshopDetails,
    isCustomerWithExistingVehiclePolicy
} from './shared/vehicle';

export const getNextPage: NextFunction = async (curPageId: string, formConfig: FormConfig, formValues: FormValues) => {
    let nextPage: string | undefined = '';
    const isVehicleAtWorkshop =
        getValueOrDefault(formConfig, formValues, 'fileUpload').customerDidNotUploadReason ===
        'Køretøjet er allerede på værksted';

    switch (curPageId) {
        //#region Landing page
        case 'godt-at-vide':
            nextPage = 'dato';
            break;
        //#endregion

        //#region Step 1: About the damage
        case 'dato':
            nextPage = 'tidspunkt';
            break;
        case 'tidspunkt':
            nextPage = 'adresse';
            break;
        case 'adresse':
            nextPage = 'beskriv-skaden';
            break;
        case 'beskriv-skaden':
            nextPage = 'vaelg-koeretoej';
            break;
        case 'vaelg-koeretoej':
            nextPage = 'andet-koeretoej-nummerplade';

            if (getValueOrDefault(formConfig, formValues, 'registrationNumber').registrationNumber) {
                nextPage = 'situation';
            }
            break;
        case 'andet-koeretoej-nummerplade':
            nextPage = 'om-andet-koeretoej';
            break;
        case 'om-andet-koeretoej':
            nextPage = 'situation';
            break;
        case 'situation':
            nextPage = 'ansvar';
            break;
        case 'ansvar':
            nextPage = 'hvad-er-skadet';
            break;
        case 'hvad-er-skadet':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skadezoner':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skader-andet-koeretoej':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skade-andre-koertoejer':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'anden-persons-koeretoej':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'personskade':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'oplysninger-om-passagerer':
            nextPage = getNextDamagePage(curPageId, formConfig, formValues);
            break;
        case 'skade-ting':
            nextPage = 'foerer';

            const isReadyToFileAllThingsDamaged =
                getValueOrDefault(formConfig, formValues, 'areYouReadyToRegister').value === 'yes';
            if (isReadyToFileAllThingsDamaged) {
                nextPage = 'genstandsregistrering';
            }

            break;
        case 'genstandsregistrering':
            nextPage = 'foerer';
            break;
        //#endregion

        //#region Step 2: About the driver
        case 'foerer':
            nextPage = 'paavirkning';

            const selectedDriver = getValueOrDefault(formConfig, formValues, 'whoDroveYourVehicle').value;
            const driverOptionsWhereExtraInfoIsRequired = [
                'myPartner',
                'someoneFromMyHousehold',
                'someoneOutsideOfMyHousehold'
            ];

            if (driverOptionsWhereExtraInfoIsRequired.includes(selectedDriver)) {
                nextPage = 'foerer-oplysninger';
            }

            break;
        // Page 9A: About the driver (spouse/partner)
        // Page 9B: About the driver (someone from my household)
        // Page 9C: About the driver (someone outside of my household)
        case 'foerer-oplysninger':
            nextPage = 'paavirkning';
            break;
        // Page 10: About the driver (under influence of drugs)
        case 'paavirkning':
            nextPage = 'oplysninger-om-modpart';
            break;
        //#endregion

        //#region Step 3: Counterpart
        case 'oplysninger-om-modpart':
            nextPage = 'dokumentation';

            if (getValueOrDefault(formConfig, formValues, 'doYouHaveInformationAboutOthersInvolved').value === 'yes') {
                nextPage = 'modpartens-rolle-1';
            }

            break;
        // Page 11A: Counterpart (counterparts role, person 1-4)
        case 'modpartens-rolle-1':
            nextPage = 'om-modparten-1';
            break;
        case 'modpartens-rolle-2':
            nextPage = 'om-modparten-2';
            break;
        case 'modpartens-rolle-3':
            nextPage = 'om-modparten-3';
            break;
        // Page 11B: Counterpart (details, person 1-4)
        case 'om-modparten-1':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson2',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-2'
                : 'dokumentation';
            break;
        case 'om-modparten-2':
            nextPage = isAddAnotherPersonChecked(
                'doYouHaveInformationAboutOthersInvolvedAddMorePersons-addPerson3',
                formConfig,
                formValues
            )
                ? 'modpartens-rolle-3'
                : 'dokumentation';
            break;
        case 'om-modparten-3':
            nextPage = 'dokumentation';
            break;
        //#endregion

        //#region Step 4: Documentation
        case 'dokumentation':
            nextPage = 'politi-og-samtykke';
            break;
        case 'politi-og-samtykke':
            if (getValueOrDefault(formConfig, formValues, 'policeContacted').value === 'Y') {
                nextPage = 'samtykke';
            } else if (
                isCustomerClaimCovered(
                    getClaimDetails(formConfig, formValues),
                    getWorkshopDetails(formConfig, formValues)
                ) &&
                !isVehicleAtWorkshop
            ) {
                nextPage = 'du-er-daekket';
            } else if (
                isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cpr') &&
                !isVehicleAtWorkshop
            ) {
                nextPage = 'hvilken-skade';
            } else {
                nextPage = 'om-dig';
            }
            break;
        case 'samtykke':
            if (
                isCustomerClaimCovered(
                    getClaimDetails(formConfig, formValues),
                    getWorkshopDetails(formConfig, formValues)
                ) &&
                !isVehicleAtWorkshop
            ) {
                nextPage = 'du-er-daekket';
            } else if (
                isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cpr') &&
                !isVehicleAtWorkshop
            ) {
                nextPage = 'hvilken-skade';
            } else {
                nextPage = 'om-dig';
            }
            break;

        case 'hvilken-skade':
            nextPage = 'find-vaerksted';
            break;
        //#endregion

        //#region Step 5: Workshop
        case 'du-er-daekket':
            if (
                isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cpr') &&
                !isVehicleAtWorkshop
            ) {
                nextPage = 'hvilken-skade';
            } else {
                nextPage = 'om-dig';
            }
            break;

        case 'find-vaerksted':
            nextPage = 'om-dig';
            break;
        //#endregion

        //#region Step 6: About you
        case 'om-dig':
            nextPage = 'resume';
            break;
        //#endregion

        //#region Summery
        case 'resume':
            nextPage = undefined;
            break;
        //#endregion

        default:
            console.log('# curPageId=', curPageId);
            throw new Error('Unknown page: ' + curPageId);
    }

    return nextPage;
};

/**
 * Decides which damage page that are the next to show, depending on where in the form flow the user is, and which damage checkboxes that have been selected.
 * @param currentPageId The id of the current page
 * @param formConfig
 * @param formValues
 * @returns Next damage page to show.
 */
const getNextDamagePage = (
    currentPageId: string,
    formConfig: FormConfig,
    formValues: FormValues
): string | undefined => {
    const damageSelections = getWhatHasBeenDamaged(formConfig, formValues);

    switch (currentPageId) {
        case 'hvad-er-skadet':
            if (damageSelections.vehicle.isMyVehicleDamaged) {
                // The 'other' vehicle type can be selected in one of two ways:
                // 1. For a customer with a policy:
                //    Customer first selects 'Other' in the customerVehicle component, followed up by selecting 'Other' a second time in the radiobuttons list on the next page.
                //    Because the option selected in the radiobuttons list is not reset if user navigates back in the flow, we also need to check the registrationNumber value.
                // 2. For a customer without a policy (non policy case):
                //    The customerVehicle component will render vehicle options where 'Other' is one of the options.
                const isVehicleTypeOtherSelected = isCustomerWithExistingVehiclePolicy(formConfig, formValues)
                    ? getValueOrDefault(formConfig, formValues, 'registrationNumber').registrationNumber === '' &&
                      getValueOrDefault(formConfig, formValues, 'vehicleTypeOther').value === 'other'
                    : getValueOrDefault(formConfig, formValues, 'registrationNumber').entityType === 'others';

                // If a customer has selected 'other' as vehicle type -> skip the skadezoner page
                if (isVehicleTypeOtherSelected) {
                    return 'skader-andet-koeretoej';
                }

                return 'skadezoner';
            }
            if (damageSelections.vehicle.isOthersVehicleDamaged) return 'skade-andre-koertoejer';
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
        case 'skader-andet-koeretoej':
        case 'skadezoner':
            if (damageSelections.vehicle.isOthersVehicleDamaged) return 'skade-andre-koertoejer';
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
        case 'skade-andre-koertoejer':
            const isSingleCounterpartVehicleDamaged =
                getValueOrDefault(formConfig, formValues, 'howManyOtherVehiclesAreDamaged').value === 'aSingleVehicle';

            if (isSingleCounterpartVehicleDamaged) {
                return 'anden-persons-koeretoej';
            }

            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
        case 'anden-persons-koeretoej':
            if (damageSelections.persons.isDriverDamaged) return 'personskade';
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
        case 'personskade':
            if (damageSelections.persons.isPassangersDamaged) return 'oplysninger-om-passagerer';
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
        case 'oplysninger-om-passagerer':
            if (damageSelections.things.isMyThingsDamaged) return 'skade-ting';
            return 'foerer';
    }
};

/**
 * Determines if a given 'add another person' checkbox element is selected or not.
 * @param elementId Id of the 'add another person' checkbox element
 * @param formConfig
 * @param formValues
 * @returns True if the 'add another person' checkbox is selected, else false.
 */
const isAddAnotherPersonChecked = (elementId: string, formConfig: FormConfig, formValues: FormValues): boolean => {
    const addAnotherPersonCheckbox = getValueOrDefault(formConfig, formValues, elementId);
    return addAnotherPersonCheckbox && addAnotherPersonCheckbox.length > 0
        ? addAnotherPersonCheckbox[0].value === 'yes'
        : false;
};
